import { create } from 'zustand';
import { collectDeviceData } from '@/shared/api/allowme';

/**
 * AllowMe State Interface
 */
interface AllowMeState {
  /** Collected device fingerprint or `null` if not yet available */
  deviceCollectId: string | null;
  /** Whether collection is in progress */
  isCollecting: boolean;
  /** Error message from the last collection attempt */
  error: string | null;
}

/**
 * AllowMe Actions Interface
 */
interface AllowMeActions {
  /** Collect device fingerprint with timeout protection */
  collect: (eventName?: string) => Promise<void>;
  /** Reset store to initial state */
  reset: () => void;
}

/**
 * AllowMe Store Type
 */
export interface AllowMeStore extends AllowMeState, AllowMeActions {}

const initialState: AllowMeState = {
  deviceCollectId: null,
  isCollecting: false,
  error: null,
};

/**
 * AllowMe Store
 *
 * Manages the AllowMe SDK device fingerprint collection state.
 *
 * Collection is triggered proactively at App mount so the data is
 * available by the time the user submits the login form.
 * Includes a timeout to ensure the app is never blocked by a slow SDK.
 *
 * @example
 * ```tsx
 * // Trigger collection (typically in App root via useAllowMeCollect)
 * useAllowMeStore.getState().collect('login');
 *
 * // Read cached data at login time
 * const { deviceCollectId } = useAllowMeStore.getState();
 * ```
 */
let pendingPromise: Promise<void> | null = null;

function createAllowMeStore(
  set: (state: Partial<AllowMeStore>) => void,
  get: () => AllowMeStore
): AllowMeStore {
  return {
    ...initialState,

    collect: (eventName?: string): Promise<void> => {
      if (get().deviceCollectId) return Promise.resolve();

      // Don't retry after a previous failure — let login proceed immediately
      if (get().error) return Promise.resolve();

      // Share the same promise for concurrent callers
      if (pendingPromise) return pendingPromise;

      set({ isCollecting: true, error: null });

      pendingPromise = collectDeviceData(eventName)
        .then((data) => {
          set({ deviceCollectId: data, isCollecting: false });
        })
        .catch((err) => {
          console.warn('[AllowMe] collect failed:', (err as Error).message);
          set({
            error: (err as Error).message,
            isCollecting: false,
          });
        })
        .finally(() => {
          pendingPromise = null;
        });

      return pendingPromise;
    },

    reset: () => {
      pendingPromise = null;
      set(initialState);
    },
  };
}

export const useAllowMeStore = create<AllowMeStore>()(createAllowMeStore);
